import { computationOptions } from "#app/computationOptions";
import {
  getQuoteRequestLineState,
  isQuoteRequestLineStateProcessing,
} from "#modules/quoteRequestLines/domain/getQuoteRequestLineState";
import { getIsAnalyzingAssembly } from "#modules/quoteRequestLines/helpers/getIsAnalyazingAssembly";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { getComputedQuoteRequestLinePrice } from "#modules/quoteRequestLinesV2/domain/getComputedQuoteRequestLinePrice";
import {
  AssemblyWithChildren,
  computeAssemblyPrice,
  OptimizedMachineTimes,
  OptimizedMaterialCosts,
  PredictionModelTypeType,
  sortAssembliesByCreatedAt,
  sortQuoteRequestLines,
} from "shared";

export type PredictionStrategy = {
  isClustering: boolean;
  modelType: PredictionModelTypeType | null;
};

export type QuoteRequestLineComputedPrice = NonNullable<
  ReturnType<typeof getComputedQuoteRequestLinePrice>
>;
export type QuoteRequestLineAssemblyPrice = ReturnType<typeof computeAssemblyPrice>["assembly"];

export type LoadedQuoteRequestLineTableRow = {
  line: QuoteRequestLineDto;
  isLoadingRow?: false;
  isBatchSiblingRow?: false;
  assembly: AssemblyWithChildren<QuoteRequestLineDto>;
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[];
  depth: number;
  isAssemblyParent: boolean;
  childrenCount: number;
  linePrice: QuoteRequestLineComputedPrice | null;
  assemblyPrice: QuoteRequestLineAssemblyPrice | null;
  isAnalyzingOrPredicting: boolean;
  isAssemblyAnalyzing: boolean;
};

export type BatchSiblingQuoteRequestLineTableRow = {
  line: QuoteRequestLineDto;
  isLoadingRow?: false;
  isBatchSiblingRow: true;
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[];
  isAssemblyParent: boolean;
  linePrice: QuoteRequestLineComputedPrice | null;
  assemblyPrice: QuoteRequestLineAssemblyPrice | null;
  isAnalyzingOrPredicting: boolean;
};

type LoadingQuoteRequestLineTableRow = {
  isLoadingRow: true;
  isBatchSiblingRow?: false;
  line: { id: string };
};

export type QuoteRequestLineTableRow =
  | LoadedQuoteRequestLineTableRow
  | BatchSiblingQuoteRequestLineTableRow
  | LoadingQuoteRequestLineTableRow;

export const isLoadedQuoteRequestLineTableRow = (
  row: QuoteRequestLineTableRow,
): row is LoadedQuoteRequestLineTableRow => !row.isLoadingRow && !row.isBatchSiblingRow;

export const isBatchSiblingQuoteRequestLineTableRow = (
  row: QuoteRequestLineTableRow,
): row is BatchSiblingQuoteRequestLineTableRow => !row.isLoadingRow && !!row.isBatchSiblingRow;

export const isLoadingQuoteRequestLineTableRow = (
  row: QuoteRequestLineTableRow,
): row is LoadingQuoteRequestLineTableRow => !!row.isLoadingRow;

export const sortByCreatedAt = sortAssembliesByCreatedAt;

export const isLastRowInBatchGroup = (row: QuoteRequestLineTableRow): boolean => {
  if (isBatchSiblingQuoteRequestLineTableRow(row)) {
    const newestAssembly = sortByCreatedAt(row.assembliesInBatch).at(-1);
    return newestAssembly?.quoteRequestLine.id === row.line.id;
  }
  if (isLoadedQuoteRequestLineTableRow(row)) {
    return row.assembliesInBatch.length <= 1;
  }
  return true;
};

export const getBatchGroupKey = (row: QuoteRequestLineTableRow): string | undefined => {
  if (isBatchSiblingQuoteRequestLineTableRow(row)) {
    return sortByCreatedAt(row.assembliesInBatch).at(0)?.quoteRequestLine.id;
  }
  if (isLoadedQuoteRequestLineTableRow(row) && row.assembliesInBatch.length > 1) {
    return sortByCreatedAt(row.assembliesInBatch).at(0)?.quoteRequestLine.id;
  }
  return undefined;
};

const ROOT_DRAG_GROUP_KEY = "__root__";
const CHILDREN_DRAG_GROUP_KEY = "__children__";
const NO_DRAG_GROUP_KEY = "__nodrag__";

export const getQuoteRequestLineRowDragGroupKey = (
  row: QuoteRequestLineTableRow,
): string | undefined => {
  if (!isLoadedQuoteRequestLineTableRow(row)) {
    return `${NO_DRAG_GROUP_KEY}:${row.line.id}`;
  }
  return row.depth === 0
    ? ROOT_DRAG_GROUP_KEY
    : `${CHILDREN_DRAG_GROUP_KEY}:${String(row.line.assemblyParentId)}`;
};

export const canDragQuoteRequestLineRow =
  (disabled: boolean) =>
  (row: QuoteRequestLineTableRow): boolean =>
    !disabled && isLoadedQuoteRequestLineTableRow(row);

const isAssemblySubtreeProcessing = (
  assembly: AssemblyWithChildren<QuoteRequestLineDto>,
  isChild: boolean,
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[],
  strategy: PredictionStrategy,
): boolean => {
  const state = getQuoteRequestLineState({
    quoteRequestLine: assembly.quoteRequestLine,
    part: assembly.quoteRequestLine.part,
    materialGrade: assembly.quoteRequestLine.materialGrade,
    isChild,
    hasChildren: assembly.children.length > 0,
    assembliesInBatch,
    currentAssemblyChildren: assembly.children,
    ...strategy,
  });
  if (isQuoteRequestLineStateProcessing(state)) {
    return true;
  }
  return assembly.children.some((child) =>
    isAssemblySubtreeProcessing(child, true, [child], strategy),
  );
};

const isAssemblyChildrenAnalyzing = (
  assembly: AssemblyWithChildren<QuoteRequestLineDto>,
): boolean => {
  if (getIsAnalyzingAssembly(assembly.quoteRequestLine.part)) {
    return true;
  }
  return assembly.children.some(isAssemblyChildrenAnalyzing);
};

const computeLinePricing = (
  assembly: AssemblyWithChildren<QuoteRequestLineDto>,
  isChild: boolean,
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[],
  strategy: PredictionStrategy,
  optimizedMachineTimes: OptimizedMachineTimes,
  optimizedMaterialCosts: OptimizedMaterialCosts,
) => {
  const line = assembly.quoteRequestLine;
  const isAssemblyParent = assembly.children.length > 0;
  const linePrice = getComputedQuoteRequestLinePrice(
    line,
    optimizedMachineTimes,
    optimizedMaterialCosts,
  );
  const assemblyPrice =
    linePrice && isAssemblyParent
      ? computeAssemblyPrice(
          {
            children: assembly.children,
            quoteRequestLine: line,
            salesMarginRatio: 0,
            optimizedMachineTimes,
            optimizedMaterialCosts,
          },
          computationOptions,
        ).assembly
      : null;
  const isAssemblyAnalyzing = isAssemblyChildrenAnalyzing(assembly);
  const isAnalyzingOrPredicting =
    isAssemblySubtreeProcessing(assembly, isChild, assembliesInBatch, strategy) ||
    isAssemblyAnalyzing;

  return {
    isAssemblyParent,
    linePrice,
    assemblyPrice,
    isAnalyzingOrPredicting,
    isAssemblyAnalyzing,
  };
};

const pushRow = (
  assembly: AssemblyWithChildren<QuoteRequestLineDto>,
  depth: number,
  rows: QuoteRequestLineTableRow[],
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[],
  strategy: PredictionStrategy,
  optimizedMachineTimes: OptimizedMachineTimes,
  optimizedMaterialCosts: OptimizedMaterialCosts,
) => {
  const line = assembly.quoteRequestLine;
  const {
    isAssemblyParent,
    linePrice,
    assemblyPrice,
    isAnalyzingOrPredicting,
    isAssemblyAnalyzing,
  } = computeLinePricing(
    assembly,
    depth > 0,
    assembliesInBatch,
    strategy,
    optimizedMachineTimes,
    optimizedMaterialCosts,
  );

  rows.push({
    line,
    assembly,
    assembliesInBatch,
    depth,
    isAssemblyParent,
    childrenCount: assembly.children.length,
    linePrice,
    assemblyPrice,
    isAnalyzingOrPredicting,
    isAssemblyAnalyzing,
  });
};

const pushChildrenRowsAfterBatch = (
  assembly: AssemblyWithChildren<QuoteRequestLineDto>,
  depth: number,
  expandedAssemblyIds: ReadonlySet<string>,
  rows: QuoteRequestLineTableRow[],
  visibleLineIds: ReadonlySet<string> | undefined,
  strategy: PredictionStrategy,
  optimizedMachineTimes: OptimizedMachineTimes,
  optimizedMaterialCosts: OptimizedMaterialCosts,
) => {
  if (assembly.children.length === 0 || !expandedAssemblyIds.has(assembly.quoteRequestLine.id)) {
    return;
  }

  assembly.children.forEach((child) => {
    if (visibleLineIds && !visibleLineIds.has(child.quoteRequestLine.id)) {
      return;
    }
    pushRow(child, depth, rows, [child], strategy, optimizedMachineTimes, optimizedMaterialCosts);
    pushChildrenRowsAfterBatch(
      child,
      depth + 1,
      expandedAssemblyIds,
      rows,
      visibleLineIds,
      strategy,
      optimizedMachineTimes,
      optimizedMaterialCosts,
    );
  });
};

export const buildQuoteRequestLineTableRows = (
  quoteRequestLines: QuoteRequestLineDto[],
  expandedAssemblyIds: ReadonlySet<string>,
  strategy: PredictionStrategy,
  optimizedMachineTimes: OptimizedMachineTimes,
  optimizedMaterialCosts: OptimizedMaterialCosts,
  visibleLineIds?: ReadonlySet<string>,
): QuoteRequestLineTableRow[] => {
  const { batches } = sortQuoteRequestLines(quoteRequestLines);
  const isVisible = (id: string) => !visibleLineIds || visibleLineIds.has(id);

  const rows: QuoteRequestLineTableRow[] = [];
  batches.forEach((assembliesInBatch) => {
    const [mainAssembly, ...siblingAssemblies] = sortByCreatedAt(assembliesInBatch);
    if (!mainAssembly) {
      return;
    }

    if (isVisible(mainAssembly.quoteRequestLine.id)) {
      pushRow(
        mainAssembly,
        0,
        rows,
        assembliesInBatch,
        strategy,
        optimizedMachineTimes,
        optimizedMaterialCosts,
      );
    }

    siblingAssemblies.forEach((assembly) => {
      if (!isVisible(assembly.quoteRequestLine.id)) {
        return;
      }
      const { isAssemblyParent, linePrice, assemblyPrice, isAnalyzingOrPredicting } =
        computeLinePricing(
          assembly,
          false,
          assembliesInBatch,
          strategy,
          optimizedMachineTimes,
          optimizedMaterialCosts,
        );
      rows.push({
        line: assembly.quoteRequestLine,
        isBatchSiblingRow: true,
        assembliesInBatch,
        isAssemblyParent,
        linePrice,
        assemblyPrice,
        isAnalyzingOrPredicting,
      });
    });

    pushChildrenRowsAfterBatch(
      mainAssembly,
      1,
      expandedAssemblyIds,
      rows,
      visibleLineIds,
      strategy,
      optimizedMachineTimes,
      optimizedMaterialCosts,
    );
  });

  return rows;
};
